import type { IChangeEmail, IUpdateUser, IUpdateUserProfile } from '@escloud/types';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { useApi, useTenant } from '@/context';
import { DEFAULT_REGION } from '@/lib/region-config';

export const userKeys = {
    all: ['users'] as const,
    /**
     * The user row is per region — each regional API has its own, JIT-provisioned
     * on first contact — so the region is part of the cache identity just as the
     * tenant is for tenant-scoped queries (ESCLOUD-311).
     */
    me: (region: string) => [...userKeys.all, 'me', region] as const,
    meAll: () => [...userKeys.all, 'me'] as const,
    /** Per region for the same reason the user row is — it answers about that row. */
    onboardingState: (region: string) => [...userKeys.onboardingStateAll(), region] as const,
    /** Prefix — invalidating it re-asks every region. */
    onboardingStateAll: () => [...userKeys.meAll(), 'onboarding-state'] as const,
    /**
     * Prefix only — memberships are always fetched per region, because the
     * endpoint is region-local. Invalidating this key refreshes every region.
     */
    tenants: () => [...userKeys.meAll(), 'tenants'] as const,
    /** Memberships as one region's API sees them — the endpoint is region-local. */
    tenantsInRegion: (region: string) => [...userKeys.tenants(), region] as const,
};

export function useCurrentUser() {
    const api = useApi();
    const { tenantRegion } = useTenant();
    const region = tenantRegion ?? DEFAULT_REGION;

    return useQuery({
        queryKey: userKeys.me(region),
        queryFn: () => api.users.getCurrentUser(),
    });
}

/**
 * What the server still wants from this user (ESCLOUD-278): whether the
 * professional profile counts as complete, which required fields are still
 * empty, and what documents are owed.
 *
 * Completeness is DERIVED server-side from the current required-field list, so
 * this is the only place a client gate may take it from. Reading
 * `profileCompletedAt` off the user row instead makes the client's answer drift
 * from the server's the moment that list widens — the dialog would wave a user
 * through and every clinical request would then 403.
 */
export function useOnboardingState() {
    const api = useApi();
    const { tenantRegion } = useTenant();
    const region = tenantRegion ?? DEFAULT_REGION;

    return useQuery({
        queryKey: userKeys.onboardingState(region),
        queryFn: () => api.users.getOnboardingState(),
        // Same reasoning as the acceptance gate query it sits beside: this
        // blocks the whole clinical UI, so avoid refetch churn. Freshness comes
        // from the explicit invalidation on a profile write, not from polling.
        staleTime: 5 * 60 * 1000,
    });
}

export function useUpdateCurrentUser() {
    const api = useApi();
    const queryClient = useQueryClient();
    const { tenantRegion } = useTenant();

    return useMutation({
        mutationFn: (data: IUpdateUser) => api.users.updateCurrentUser(data),
        onSuccess: (updatedUser) => {
            queryClient.setQueryData(userKeys.me(tenantRegion ?? DEFAULT_REGION), updatedUser);
        },
    });
}

/**
 * Professional profile / consent update (`PATCH /users/me/profile`). The row
 * is per region, so the fresh answer replaces the cache entry of the region
 * the request actually went to. `region` targets a specific regional API: the
 * funnel has no tenant in context yet, so it names the intent's region
 * explicitly (ESCLOUD-332).
 */
export function useUpdateProfile(options: { region?: string } = {}) {
    const api = useApi();
    const queryClient = useQueryClient();
    const { tenantRegion } = useTenant();

    return useMutation({
        mutationFn: (data: IUpdateUserProfile) => api.users.updateProfile(data, options.region),
        onSuccess: (updatedUser) => {
            const region = options.region ?? tenantRegion ?? DEFAULT_REGION;
            queryClient.setQueryData(userKeys.me(region), updatedUser);
            // Completeness is the server's to recompute, so the write cannot be
            // reflected by patching a cached flag — it has to be re-asked. This
            // is what closes the device-use gate after the profile is submitted.
            void queryClient.invalidateQueries({ queryKey: userKeys.onboardingState(region) });
        },
    });
}

/**
 * Replace the sign-in address while it is unverified (funnel v2). `region` is
 * the intent's region during the funnel; the account banner (ESCLOUD-333) passes
 * the tenant's. The claims still carry the old address until `refreshToken()`,
 * which the caller runs after this succeeds.
 */
export function useChangeEmail(region: string | null) {
    const api = useApi();
    const queryClient = useQueryClient();
    const { tenantRegion } = useTenant();

    return useMutation({
        mutationFn: (data: IChangeEmail) => api.users.changeEmail(data, region ?? undefined),
        onSuccess: (updatedUser) => {
            queryClient.setQueryData(userKeys.me(region ?? tenantRegion ?? DEFAULT_REGION), updatedUser);
        },
    });
}

/**
 * Verification-email resend (ESCLOUD-286). No cache to touch — the answer is
 * an accepted request, and the state that matters (the verified flag) only
 * changes through a token refresh.
 */
export function useResendVerificationEmail() {
    const api = useApi();
    return useMutation({
        mutationFn: () => api.users.resendVerificationEmail(),
    });
}
